import React from 'react'
import PropTypes from 'prop-types'
import * as Component from './Grid.styles'

// The responsive layout grid adapts to screen and orientation, ensuring consistency across layouts
const Grid = (props) => {
  const { xs, sm, md, td, lg, xl, print, spacing, container, ...other } = props

  return (
    <Component.Root
      xs={ xs }
      sm={ sm }
      md={ md }
      td={ td }
      lg={ lg }
      xl={ xl }
      print={ print }
      spacing={ spacing }
      container={ container }
      { ...other }
    />
  )
}

Grid.defaultProps = {
  xs: false,
  sm: false,
  md: false,
  td: false,
  lg: false,
  xl: false,
  containerHeight: {
    value: false,
    unit: false,
  },
  print: false,
  container: false,
  item: false,
}

Grid.propTypes = {
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for all the screen sizes with the lowest priority.
   */
  xs: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `sm` breakpoint and wider screens if not overridden.
   */
  sm: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `md` breakpoint and wider screens if not overridden.
   */
  md: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `td` breakpoint and wider screens if not overridden.
   */
  td: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `lg` breakpoint and wider screens if not overridden.
   */
  lg: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `xl` breakpoint and wider screens.
   */
  xl: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the media query print.
   */
  print: PropTypes.oneOf([
    false,
    'auto',
    true,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ]),
  /**
   * Defines the space between the type `item` component.
   * It can only be used on a type `container` component.
   */
  spacing: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
  /**
   * If true, the component will have the flex *container* behavior.
   * You should be wrapping *items* with a *container*.
   */
  container: PropTypes.bool,
  /**
   * If true, the component will have the flex *item* behaviour
   * You should be wrapping *items* with a *container*.
   */
  item: PropTypes.bool,
  /**
   * Defines the `align-items` style property.
   * It's applied for all screen sizes.
   */
  alignItems: PropTypes.oneOf([
    'normal',
    'baseline',
    'center',
    'flex-end',
    'flex-start',
    'stretch',
  ]),
  /**
   * Defines the `justify-content` style property.
   * It's applied for all screen sizes.
   */
  justifyContent: PropTypes.oneOf([
    'normal',
    'baseline',
    'center',
    'flex-end',
    'flex-start',
    'stretch',
  ]),
  /**
   * Defines the `align-self` style property.
   * It's applied for all screen sizes.
   */
  alignSelf: PropTypes.oneOf([
    'auto',
    'baseline',
    'center',
    'flex-end',
    'flex-start',
    'stretch',
  ]),
  /**
   * Defines the `height` style property.
   * It's applied for all screen sizes.
   */
  containerHeight: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf([false, 'auto']),
    ]),
    unit: PropTypes.oneOf([false, 'pt', 'px', 'em', '%', 'vh', 'vw']),
  }),
}

export default Grid
